import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ROUTES } from "utils/constants";
import { useHistory } from "react-router-dom";
import { AppState } from "store";
import { Table, TablePagination } from "components/base";
import { parseBatteryHeartbeatTable, parseBatteryTable } from "utils/parsers";
import {
  initializeVehicleModel,
  setIssuesSelectedPage,
  initializeBatteries,
  setSelectedBattery,
  setBatterySelectedPage,
  setBatteryPage,
  setModels,
  getClients,
  setDynamicFilters,
  setClientDynamicFilters,
  setFilterBatteryModels,
  setDynamicBatteryModelFilters,
} from "store/actions";
import { ActionMenu, functionalStatusCell } from "./cell-renders";
import { TableProps } from "antd/lib/table";
import { Card, Tag, Avatar, Progress, Button, Checkbox } from "antd";
import {
  ThunderboltOutlined,
  RightOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import "styles/components/tab-content/battery-content/battery-content.scss";
import useDebounce from "../../../utils/hooks";


const isStaleBattery = (updatedAt?: string) => {
  if (!updatedAt) return false;

  const updatedTime = new Date(updatedAt).getTime();
  const now = Date.now();

  const diffInDays = (now - updatedTime) / (1000 * 60 * 60 * 24);

  return diffInDays >= 5;
};


const getColumns: () => TableProps<any>["columns"] = () => {
  const columns: TableProps<any>["columns"] = [
    {
      title: "Battery Alias",
      dataIndex: "battery_alias",
      width: 130,
      ellipsis: true,
      render: (text, record: any) => {
        const stale = isStaleBattery(record.updated_at);

        return (
            <span style={{ fontWeight: stale ? 600 : 400 }}>
        {text || "N/A"}
              {stale && <span style={{ marginLeft: 6 }}>⚠️</span>}
      </span>
        );
      },
    },
    {
      title: "SoC",
      dataIndex: "soc_level",
      width: 80,
      ellipsis: true,
    },
    {
      title: "Host ID",
      dataIndex: "host_id",
      width: 160,
      ellipsis: true,
    },
    {
      title: "Operational State",
      dataIndex: "operational_state",
      width: 160,
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: functionalStatusCell,
      width: 150,
      ellipsis: true,
    },
    {
      title: "Battery ID",
      dataIndex: "battery_id",
      width: 160,
      ellipsis: true,
      sorter: true,
    },
    {
      title: "SoH",
      dataIndex: "soh_level",
      width: 100,
      ellipsis: true,
      align: "center",
    },
    {
      title: "Model",
      dataIndex: "make_model",
      width: 100,
      ellipsis: true,
      align: "center",
    },

    {
      title: "Last Ping",
      dataIndex: "updated_at",
      width: 150,
      ellipsis: true,
    },
    {
      title: "",
      dataIndex: "action",
      render: ActionMenu,
      align: "right",
      className: "action-menu open-link --issues",
    },
  ];
  return columns;
};

// Mobile Battery Card Component
interface BatteryCardProps {
  battery: any;
  onClick: () => void;
  isSelected?: boolean;
  onSelect?: (checked: boolean) => void;
  showSelection?: boolean;
}

const BatteryCard: React.FC<BatteryCardProps> = ({
  battery,
  onClick,
  isSelected = false,
  onSelect,
  showSelection = false,
}) => {
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "online":
      case "healthy":
        return "green";
      case "inactive":
      case "offline":
      case "unhealthy":
        return "red";
      case "maintenance":
      case "charging":
        return "orange";
      case "idle":
        return "blue";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "online":
      case "healthy":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "inactive":
      case "offline":
      case "unhealthy":
        return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
      case "charging":
        return <SyncOutlined spin style={{ color: "#faad14" }} />;
      default:
        return <ThunderboltOutlined style={{ color: "#1890ff" }} />;
    }
  };

  const getSocColor = (soc: number) => {
    if (soc >= 70) return "#52c41a";
    if (soc >= 30) return "#faad14";
    return "#ff4d4f";
  };

  const socValue = parseInt(battery.soc_level) || 0;
  const sohValue = parseInt(battery.soh_level) || 0;
  const isStale = isStaleBattery(battery.updated_at);

  return (
    <Card
      className={`battery-mobile-card ${isSelected ? "selected" : ""} ${isStale ? "stale-card" : ""}`}
      onClick={onClick}
    >
      <div className="card-header">
        {showSelection && (
          <Checkbox
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect?.(e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            className="card-checkbox"
          />
        )}
        <div className="battery-avatar">
          <Avatar
            icon={<ThunderboltOutlined />}
            size={48}
            style={{
              background: `linear-gradient(135deg, ${getSocColor(socValue)} 0%, #1890ff 100%)`,
            }}
          />
        </div>
        <div className="battery-main-info">
          <div className="battery-alias-wrapper">
            <h4 className="battery-alias">
              {battery.battery_alias || battery.battery_id || "N/A"}
              {isStale && <span className="stale-indicator">⚠️</span>}
            </h4>
          </div>
          <span className="battery-id">{battery.battery_id || "N/A"}</span>
        </div>
        <div 
          className="card-action-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          {ActionMenu(battery.action, battery)}
        </div>
      </div>

      <div className="card-body">
        {/* SoC & SoH Progress */}
        <div className="battery-levels">
          <div className="level-item">
            <div className="level-header">
              <span className="level-label">SoC</span>
              <span className="level-value">{battery.soc_level || "0%"}</span>
            </div>
            <Progress
              percent={socValue}
              size="small"
              strokeColor={getSocColor(socValue)}
              showInfo={false}
            />
          </div>
          <div className="level-item">
            <div className="level-header">
              <span className="level-label">SoH</span>
              <span className="level-value">{battery.soh_level || "0%"}</span>
            </div>
            <Progress
              percent={sohValue}
              size="small"
              strokeColor={sohValue >= 70 ? "#52c41a" : sohValue >= 30 ? "#faad14" : "#ff4d4f"}
              showInfo={false}
            />
          </div>
        </div>

        {/* Info Rows */}
        <div className="info-grid">
          {battery.host_id && (
            <div className="info-row">
              <span className="info-label">Host ID</span>
              <span className="info-value">{battery.host_id}</span>
            </div>
          )}

          {battery.operational_state && (
            <div className="info-row">
              <span className="info-label">Operational State</span>
              <span className="info-value">{battery.operational_state}</span>
            </div>
          )}

          {battery.make_model && (
            <div className="info-row">
              <span className="info-label">Model</span>
              <span className="info-value">{battery.make_model}</span>
            </div>
          )}

          {battery.updated_at && (
            <div className="info-row">
              <span className="info-label">Last Ping</span>
              <span className="info-value">{battery.updated_at}</span>
            </div>
          )}
        </div>

        {/* Status */}
        {battery.status && (
          <div className="card-footer">
            <Tag color={getStatusColor(battery.status)} className="status-tag">
              {getStatusIcon(battery.status)}
              <span>{battery.status}</span>
            </Tag>
          </div>
        )}
      </div>
    </Card>
  );
};

export default function TableView() {
  const dispatch = useDispatch();
  const history = useHistory();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const {
    batteries,
    battery_heartbeat,
    page,
    search,
    selectedBatteryRows,
    batteryModel,
    filters: { listType, updated },
  } = useSelector<AppState, AppState["batteries"]>((state) => state.batteries);
  const { selectedPage, totalPages } = page;
  const [currentOrder, setCurrentOrder] = useState("");
  const debounceSearchString = useDebounce(search, 1000);


  const data = useMemo(
    () => parseBatteryTable(search, batteries, listType),
    [batteries, listType, debounceSearchString]
  );

  const offlineBatteriesCount = useMemo(
    () => data?.filter((v: any) => isStaleBattery(v.updated_at))?.length || 0,
    [data]
  );
  
  const data2 = useMemo(
    () => parseBatteryHeartbeatTable(battery_heartbeat),
    [batteries, listType, debounceSearchString]
  );

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle table changes including sorting
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      console.log("Sorter:", sorter);

      if (sorter.field) {
        const order =
          sorter.order === "ascend"
            ? "asc"
            : sorter.order === "descend"
            ? "desc"
            : "";
        setCurrentOrder(order);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(setFilterBatteryModels());
  }, [dispatch]);

  useEffect(() => {
    console.log("current order : ", currentOrder);
    dispatch(initializeBatteries(selectedPage, currentOrder, updated, debounceSearchString));
  }, [dispatch, debounceSearchString, selectedPage, currentOrder, updated]);

  const onChangeSelection = useCallback((value: number[]) => {
    console.log("values : ", value);
    setSelectedRows(value);
  }, []);

  // Handle mobile card selection
  const handleMobileCardSelect = (index: number, checked: boolean) => {
    let newSelectedRows: number[];
    if (checked) {
      newSelectedRows = [...selectedRows, index];
    } else {
      newSelectedRows = selectedRows.filter((row) => row !== index);
    }
    onChangeSelection(newSelectedRows);
  };

  const onBack = useCallback(() => {
    if (selectedPage > 1) {
      dispatch(setBatterySelectedPage(selectedPage - 1));
    }
  }, [dispatch, selectedPage]);

  const onNext = useCallback(() => {
    if (selectedPage < totalPages) {
      dispatch(setBatterySelectedPage(selectedPage + 1));
    }
  }, [dispatch, selectedPage, totalPages]);

  const resetCallback = useCallback(() => {
    dispatch(setBatterySelectedPage(1));
  }, [dispatch]);

  const handleRowClick = useCallback(
    (battery: any) => {
      console.log("Battery in handleRowClick:", battery.raw);
      dispatch(setSelectedBattery(battery.raw));
      history.push(`${ROUTES.battery_detail}/${battery.raw.id}`);
    },
    [dispatch, history]
  );

  return (
    <div className="battery-table-container">
      {/* Desktop/Tablet Table View */}
      <div className="desktop-table-view">
        <div className="table-scroll-wrapper">
          <Table
            columns={getColumns()}
            dataSource={data}
            rowClassName={(record: any) =>
                isStaleBattery(record.updated_at) ? "stale-row" : ""
            }
            onRow={(record) => {
              return {
                onClick: () => handleRowClick(record),
              };
            }}
            onChange={handleTableChange}
            pagination={false}
        //    scroll={{ x: "max-content", y: "70vh" }}
            className="battery-table"
          />
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="mobile-card-view">
    
        {selectedRows.length > 0 && (
          <div className="mobile-selection-bar">
            <span>{selectedRows.length} selected</span>
            <Button
              type="link"
              onClick={() => onChangeSelection([])}
              className="clear-selection-btn"
            >
              Clear
            </Button>
          </div>
        )}

        <div className="mobile-cards-list">
          {data?.map((battery: any, index: number) => (
            <BatteryCard
              key={battery.key || index}
              battery={battery}
              onClick={() => handleRowClick(battery)}
              isSelected={selectedRows.includes(index)}
              onSelect={(checked) => handleMobileCardSelect(index, checked)}
              showSelection={false}
            />
          ))}
        </div>

        {(!data || data.length === 0) && (
          <div className="no-data-message">
            <ThunderboltOutlined className="no-data-icon" />
            <p>No batteries found</p>
          </div>
        )}
      </div>
      
      <div className="bottom-bar-wrapper">
        <div className="mode-indicators">
          {offlineBatteriesCount > 0 && (
            <span className="mode-indicator offline-battery">
              <span className="indicator-icon">⚠️</span>
              Offline Battery
            </span>
          )} 
        </div>
      </div>
      
      {/* Pagination */}
      <div className="pagination-wrapper">
        <TablePagination
          page={page}
          onBack={onBack}
          onNext={onNext}
          resetCallback={resetCallback}
        />
      </div>
     
    </div>
  );
}